<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <div class="bg-white border-b sticky top-0 z-10">
      <div class="mx-auto px-4 py-4">
        <div class="tw-flex items-center justify-between">
          <div class="tw-flex items-center gap-4">
            <ds-button variant="ghost" size="icon" icon="arrow-left" @click="goBack"/>
            <div class="tw-flex items-center gap-2">
              <Icon name="comment" class="h-6 w-6 text-primary"/>
              <h1 class="text-2xl font-bold">
                {{ isNew ? 'Create Chat Widget' : (widget && widget.name) ? widget.name : 'Edit Chat Widget' }}
              </h1>
            </div>
          </div>
          <div class="tw-flex gap-2">
            <ds-button
              label="Save"
              variant="rounded"
              :disabled="isSaving || !hasChanges"
              @click="handleSave"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="mx-auto px-4 py-8">
      <div class="gap-6 tw-flex flex-row">

        <!-- ── Configuration Panel ──────────────────────────── -->
        <div class="space-y-4 w-1/3 min-w-0">

          <!-- General -->
          <div class="bg-white rounded-lg shadow-sm border">
            <div class="p-5 border-b">
              <h2 class="text-lg font-semibold">Widget Configuration</h2>
              <p class="text-gray-500 text-sm mt-0.5">Configure your chat widget settings</p>
            </div>
            <div class="p-5 space-y-5">

              <!-- Name -->
              <div class="space-y-1.5">
                <label class="block text-sm font-medium text-gray-700">
                  Widget Name <span class="text-red-500">*</span>
                </label>
                <ds-input
                  v-model="widgetName"
                  placeholder="e.g. My Ecosystem Assistant"
                  :class="nameError ? 'border-red-500' : ''"
                  @blur="validateName"
                  is-simplified
                />
                <p v-if="nameError" class="text-red-600 text-sm">{{ nameError }}</p>
              </div>

              <!-- API Key -->
              <div v-if="!isNew" class="space-y-1.5">
                <label class="block text-sm font-medium text-gray-700">API Key</label>
                <div class="tw-flex gap-2">
                  <ds-input
                    v-model="apiKey"
                    :readonly="true"
                    class="font-mono text-sm"
                    is-simplified
                  />
                  <ds-button
                    variant="rounded-secondary"
                    size="small"
                    label="Copy"
                    @click="copyApiKey"
                  />
                </div>
              </div>

            </div>
          </div>

          <!-- Appearance -->
          <div class="bg-white rounded-lg shadow-sm border">
            <div class="p-5 border-b">
              <h2 class="text-lg font-semibold">Appearance</h2>
            </div>
            <div class="p-5 space-y-5">

              <!-- Title -->
              <div class="space-y-1.5">
                <label class="block text-sm font-medium text-gray-700">Chat Title</label>
                <ds-input
                  v-model="config.title"
                  placeholder="e.g. Support Chat"
                  is-simplified
                />
              </div>

              <!-- Subtitle -->
              <div class="space-y-1.5">
                <label class="block text-sm font-medium text-gray-700">Subtitle</label>
                <ds-input
                  v-model="config.subtitle"
                  placeholder="e.g. Ask us anything"
                  is-simplified
                />
              </div>

              <!-- Header Colors -->
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-1.5">
                  <label class="block text-sm font-medium text-gray-700">Header Background</label>
                  <div class="tw-flex items-center gap-3">
                    <input
                      type="color"
                      v-model="config.primaryColor"
                      class="h-9 w-14 rounded cursor-pointer border border-gray-300 p-0.5"
                    />
                    <ds-input
                      v-model="config.primaryColor"
                      placeholder="#21244a"
                      class="font-mono"
                      is-simplified
                    />
                  </div>
                </div>
                <div class="space-y-1.5">
                  <label class="block text-sm font-medium text-gray-700">Header Text Color</label>
                  <div class="tw-flex items-center gap-3">
                    <input
                      type="color"
                      v-model="config.headerTextColor"
                      class="h-9 w-14 rounded cursor-pointer border border-gray-300 p-0.5"
                    />
                    <ds-input
                      v-model="config.headerTextColor"
                      placeholder="#ffffff"
                      class="font-mono"
                      is-simplified
                    />
                  </div>
                </div>
              </div>

              <!-- Chat Area Colors -->
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-1.5">
                  <label class="block text-sm font-medium text-gray-700">Chat Background</label>
                  <div class="tw-flex items-center gap-3">
                    <input
                      type="color"
                      v-model="config.chatBackgroundColor"
                      class="h-9 w-14 rounded cursor-pointer border border-gray-300 p-0.5"
                    />
                    <ds-input
                      v-model="config.chatBackgroundColor"
                      placeholder="#f7f8fa"
                      class="font-mono"
                      is-simplified
                    />
                  </div>
                </div>
                <div class="space-y-1.5">
                  <label class="block text-sm font-medium text-gray-700">User Message Color</label>
                  <div class="tw-flex items-center gap-3">
                    <input
                      type="color"
                      v-model="config.userMessageColor"
                      class="h-9 w-14 rounded cursor-pointer border border-gray-300 p-0.5"
                    />
                    <ds-input
                      v-model="config.userMessageColor"
                      placeholder="same as header"
                      class="font-mono"
                      is-simplified
                    />
                  </div>
                  <p class="text-xs text-gray-400">Leave empty to use the header background color.</p>
                </div>
              </div>

              <!-- Header Icon -->
              <div class="space-y-1.5">
                <label class="block text-sm font-medium text-gray-700">Header Icon URL</label>
                <div class="tw-flex items-center gap-3">
                  <div class="w-10 h-10 rounded-full border border-gray-200 bg-gray-100 tw-flex items-center justify-center shrink-0 overflow-hidden">
                    <img v-if="config.headerIconUrl" :src="config.headerIconUrl" class="w-full h-full object-cover" alt="" @error="config.headerIconUrl = ''" />
                    <Icon v-else name="comment" size="16" class="text-gray-400" />
                  </div>
                  <ds-input
                    v-model="config.headerIconUrl"
                    placeholder="https://example.com/logo.png"
                    is-simplified
                    class="tw-flex-1"
                  />
                </div>
                <p class="text-xs text-gray-400">Leave empty to use the default chat bubble icon.</p>
              </div>

              <!-- Mode -->
              <div class="space-y-1.5">
                <label class="block text-sm font-medium text-gray-700">Display Mode</label>
                <div class="grid grid-cols-2 gap-3">
                  <label
                    class="tw-flex flex-col gap-1.5 p-3 border-2 rounded-lg cursor-pointer transition-colors"
                    :class="config.mode === 'widget' ? 'border-primary bg-primary-50' : 'border-gray-200 hover:border-gray-300'"
                  >
                    <input type="radio" v-model="config.mode" value="widget" class="sr-only" style="height: 20px;"/>
                    <div class="tw-flex items-center gap-2">
                      <Icon name="comment" size="14"/>
                      <span class="text-sm font-medium">Floating button</span>
                    </div>
                    <p class="text-xs text-gray-500">FAB button in the corner of the page. Best for script-tag embeds.</p>
                  </label>
                  <label
                    class="tw-flex flex-col gap-1.5 p-3 border-2 rounded-lg cursor-pointer transition-colors"
                    :class="config.mode === 'inline' ? 'border-primary bg-primary-50' : 'border-gray-200 hover:border-gray-300'"
                  >
                    <input type="radio" v-model="config.mode" value="inline" class="sr-only" style="height: 20px;"/>
                    <div class="tw-flex items-center gap-2">
                      <Icon name="expand" size="14"/>
                      <span class="text-sm font-medium">Full chat (inline)</span>
                    </div>
                    <p class="text-xs text-gray-500">Chat fills the container. Best for iframe embeds.</p>
                  </label>
                </div>
              </div>

              <!-- Position (only relevant for widget mode) -->
              <div v-if="config.mode === 'widget'" class="space-y-1.5">
                <label class="block text-sm font-medium text-gray-700">Position</label>
                <div class="tw-flex gap-3">
                  <label class="tw-flex items-start gap-2 cursor-pointer">
                    <input type="radio" v-model="config.position" value="bottom-right" class="text-primary"/>
                    <span class="text-sm">Bottom right</span>
                  </label>
                  <label class="tw-flex items-start gap-2 cursor-pointer">
                    <input type="radio" v-model="config.position" value="bottom-left" class="text-primary"/>
                    <span class="text-sm">Bottom left</span>
                  </label>
                </div>
              </div>

            </div>
          </div>

          <!-- Conversation -->
          <div class="bg-white rounded-lg shadow-sm border">
            <div class="p-5 border-b">
              <h2 class="text-lg font-semibold">Conversation</h2>
            </div>
            <div class="p-5 space-y-5">

              <!-- Welcome message -->
              <div class="space-y-1.5">
                <label class="block text-sm font-medium text-gray-700">Welcome Message</label>
                <textarea
                  v-model="config.welcomeMessage"
                  rows="3"
                  placeholder="Hello! How can I help you today?"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
                />
              </div>

              <!-- Placeholder -->
              <div class="space-y-1.5">
                <label class="block text-sm font-medium text-gray-700">Input Placeholder</label>
                <ds-input
                  v-model="config.placeholder"
                  placeholder="Type your message..."
                  is-simplified
                />
              </div>

              <!-- RAG Focus -->
              <div class="space-y-1.5">
                <label class="block text-sm font-medium text-gray-700">AI Focus</label>
                <p class="text-xs text-gray-500">Which type of data the AI searches when answering questions</p>
                <select
                  v-model="config.ragFocus"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                >
                  <option value="none">General (no specific focus)</option>
                  <option value="organisations">Organisations / Actors</option>
                  <option value="content">Content / Knowledge base</option>
                </select>
              </div>

            </div>
          </div>

          <!-- Access -->
          <div class="bg-white rounded-lg shadow-sm border">
            <div class="p-5 border-b">
              <h2 class="text-lg font-semibold">Access & Domains</h2>
            </div>
            <div class="p-5 space-y-5">
              <div>
                <div class="tw-flex items-center justify-between mb-2">
                  <label class="block text-sm font-medium text-gray-700">Allowed Domains</label>
                  <ds-button
                    label="Add domain"
                    variant="rounded-minimal"
                    size="sm"
                    icon="plus"
                    @click="addDomain"
                  />
                </div>
                <p class="text-xs text-gray-500 mb-3">
                  Restrict the widget to specific domains. Leave empty to allow all.
                </p>
                <div class="space-y-2">
                  <div
                    v-for="(domain, index) in allowedDomains"
                    :key="index"
                    class="tw-flex gap-2"
                  >
                    <ds-input
                      v-model="allowedDomains[index]"
                      placeholder="https://example.com"
                      is-simplified
                    />
                    <ds-button
                      icon="trash"
                      icon-fill-color="black"
                      variant="rounded-minimal"
                      size="sm"
                      @click="removeDomain(index)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Status -->
          <div class="bg-white rounded-lg shadow-sm border">
            <div class="p-5 border-b">
              <h2 class="text-lg font-semibold">Status</h2>
            </div>
            <div class="p-5 space-y-3">
              <label
                v-for="option in statusOptions"
                :key="option.value"
                class="tw-flex items-start gap-3 cursor-pointer"
              >
                <input
                  type="radio"
                  v-model="config.status"
                  :value="option.value"
                  class="mt-0.5 text-primary"
                />
                <div>
                  <span class="text-sm font-medium text-gray-700">{{ option.label }}</span>
                  <p class="text-xs text-gray-500">{{ option.description }}</p>
                </div>
              </label>
            </div>
          </div>

          <!-- Save button (bottom) -->
          <ds-button
            label="Save Changes"
            variant="rounded"
            class="w-full"
            :disabled="isSaving || !hasChanges"
            @click="handleSave"
          />

        </div>

        <!-- ── Right Panel: Preview ──────────────────────────── -->
        <div class="flex-1 min-w-0">
          <div class="bg-white rounded-lg shadow-sm border sticky top-24">
            <div class="p-5 border-b tw-flex items-center justify-between">
              <div>
                <h2 class="text-lg font-semibold">Live Preview</h2>
                <p class="text-gray-500 text-sm">Preview updates after saving</p>
              </div>
              <div class="tw-flex gap-2">
                <ds-button
                  v-if="!isNew"
                  label="Copy embed code"
                  variant="rounded-minimal"
                  size="sm"
                  icon="code"
                  @click="copyIframeCode"
                />
                <ds-button
                  v-if="!isNew"
                  label="Open"
                  variant="rounded-minimal"
                  size="sm"
                  icon="external-link-alt"
                  @click="openPreview"
                />
              </div>
            </div>

            <div v-if="isNew" class="p-12 text-center text-gray-400">
              <Icon name="comment" class="w-12 h-12 mx-auto mb-3 opacity-30"/>
              <p class="text-sm">Save your widget to see a live preview</p>
            </div>

            <div v-else class="relative" style="height: 600px;">
              <iframe
                v-if="previewUrl"
                :src="previewUrl"
                class="w-full h-full border-0 rounded-b-lg"
                allow="clipboard-write"
              />
            </div>

            <!-- Embed code snippet -->
            <div v-if="!isNew" class="p-5 border-t bg-gray-50 rounded-b-lg">
              <p class="text-xs font-medium text-gray-500 mb-2">Embed on any website:</p>
              <div class="bg-white border rounded-lg p-3 font-mono text-xs text-gray-700 break-all whitespace-pre-wrap">{{ embedCode }}</div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Saved notification -->
    <Transition name="ds-notif">
      <div
        v-if="savedNotification"
        class="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-4 py-2.5 rounded-lg text-sm shadow-lg"
      >
        Widget saved successfully
      </div>
    </Transition>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'
  import Icon from '@/components/Icon/Icon.vue'
  import DsButton from '@/components/DsButton/DsButton.vue'
  import DsInput from '@/components/Form/DsInput.vue'
  import * as ChatbotWidgetsApi from '@/Domain/ChatbotWidget/Api/chatbotWidgets'

  export default defineComponent({
    name: 'ChatbotWidgetDetail',
    components: { Icon, DsButton, DsInput },
    data () {
      return {
        widget: null,
        widgetName: '',
        apiKey: '',
        config: {
          title: '',
          subtitle: '',
          primaryColor: '#21244a',
          headerTextColor: '#ffffff',
          chatBackgroundColor: '#f7f8fa',
          userMessageColor: '',
          headerIconUrl: '',
          welcomeMessage: '',
          placeholder: '',
          position: 'bottom-right',
          mode: 'widget',
          ragFocus: 'none',
          status: 'draft',
        },
        allowedDomains: [],
        nameError: '',
        isSaving: false,
        savedNotification: false,
        originalState: null,
        statusOptions: [
          { value: 'active', label: 'Active', description: 'The widget is live and accessible via embed.' },
          { value: 'draft', label: 'Draft', description: 'Only visible to you. Not accessible via embed.' },
          { value: 'inactive', label: 'Inactive', description: 'Disabled. Embed returns a 403 response.' },
        ],
      }
    },
    computed: {
      isNew () {
        return this.$route.params.id === 'new'
      },
      previewUrl () {
        if (this.isNew || !this.widget) return null
        return this.widget.embed_url
      },
      iframeEmbedCode () {
        if (!this.widget) return ''
        return `<iframe src="${this.widget.embed_url}" width="100%" height="700" frameborder="0" allow="clipboard-write"></iframe>`
      },
      scriptEmbedCode () {
        if (!this.widget) return ''
        return `<div id="ds-chat-widget" data-api-key="${this.apiKey}" data-api-url="${this.widget.ecosystem_url}"></div>\n<script src="https://static.datascouts.eu/datascouts-chat-widget.umd.js"><\/script>`
      },
      embedCode () {
        return this.config.mode === 'widget' ? this.scriptEmbedCode : this.iframeEmbedCode
      },
      hasChanges () {
        if (!this.originalState) return this.isNew
        return JSON.stringify(this.currentState) !== JSON.stringify(this.originalState)
      },
      currentState () {
        return {
          name: this.widgetName,
          config: { ...this.config },
          allowedDomains: [...this.allowedDomains],
        }
      },
    },
    async mounted () {
      if (!this.isNew) {
        await this.loadWidget()
      } else {
        this.originalState = null
      }
    },
    methods: {
      async loadWidget () {
        try {
          const id = parseInt(this.$route.params.id)
          const data = await ChatbotWidgetsApi.fetchChatbotWidget(id)
          this.widget = data
          this.widgetName = data.name || ''
          this.apiKey = data.api_key || ''
          this.config.title = data.title || ''
          this.config.subtitle = data.subtitle || ''
          this.config.primaryColor = data.primary_color || '#21244a'
          this.config.headerTextColor = data.header_text_color || '#ffffff'
          this.config.chatBackgroundColor = data.chat_background_color || '#f7f8fa'
          this.config.userMessageColor = data.user_message_color || ''
          this.config.headerIconUrl = data.header_icon_url || ''
          this.config.welcomeMessage = data.welcome_message || ''
          this.config.placeholder = data.placeholder || ''
          this.config.position = data.position || 'bottom-right'
          this.config.mode = data.mode || 'widget'
          this.config.ragFocus = data.rag_focus || 'none'
          this.config.status = data.status || 'draft'
          this.allowedDomains = data.allowed_domains?.length ? [...data.allowed_domains] : []
          this.originalState = JSON.parse(JSON.stringify(this.currentState))
        } catch (e) {
          console.error('Failed to load widget:', e)
        }
      },

      validateName () {
        if (!this.widgetName.trim()) {
          this.nameError = 'Widget name is required'
          return false
        }
        this.nameError = ''
        return true
      },

      async handleSave () {
        if (!this.validateName()) return

        this.isSaving = true
        try {
          const payload = {
            name: this.widgetName.trim(),
            title: this.config.title || null,
            subtitle: this.config.subtitle || null,
            primaryColor: this.config.primaryColor,
            headerTextColor: this.config.headerTextColor,
            chatBackgroundColor: this.config.chatBackgroundColor,
            userMessageColor: this.config.userMessageColor || null,
            headerIconUrl: this.config.headerIconUrl || null,
            welcomeMessage: this.config.welcomeMessage || null,
            placeholder: this.config.placeholder || null,
            position: this.config.position,
            mode: this.config.mode,
            ragFocus: this.config.ragFocus,
            status: this.config.status,
            allowedDomains: this.allowedDomains.filter(d => d.trim()),
          }

          if (this.isNew) {
            const response = await ChatbotWidgetsApi.createChatbotWidget(payload)
            this.widget = response.widget
            this.apiKey = response.widget.api_key
            this.$router.replace(`/settings/chatbot-widgets/${response.widget.id}`)
          } else {
            const response = await ChatbotWidgetsApi.updateChatbotWidget(parseInt(this.$route.params.id), payload)
            this.widget = response
          }

          this.originalState = JSON.parse(JSON.stringify(this.currentState))
          this.showSavedNotification()
        } catch (e) {
          console.error('Failed to save widget:', e)
        } finally {
          this.isSaving = false
        }
      },

      showSavedNotification () {
        this.savedNotification = true
        setTimeout(() => {
          this.savedNotification = false
        }, 2500)
      },

      async copyApiKey () {
        await navigator.clipboard.writeText(this.apiKey)
      },

      async copyIframeCode () {
        await navigator.clipboard.writeText(this.embedCode)
      },

      openPreview () {
        if (this.previewUrl) window.open(this.previewUrl, '_blank')
      },

      addDomain () {
        this.allowedDomains.push('')
      },

      removeDomain (index) {
        this.allowedDomains.splice(index, 1)
      },

      goBack () {
        this.$router.push('/settings/chatbot-widgets')
      },
    },
  })
</script>

<style lang="scss" scoped>
  .ds-notif-enter-active, .ds-notif-leave-active {
    transition: opacity 0.2s, transform 0.2s;
  }

  .ds-notif-enter-from, .ds-notif-leave-to {
    opacity: 0;
    transform: translateX(-50%) translateY(8px);
  }
</style>
